/**
 * Builds the Pathfinder iframe `src` and subscribes to `PATH_COMPLETE` postMessages from allowed origins.
 */
import { isPathCompleteMessage } from '../model/types';

/**
 * Joins base + path into a URL and appends non-empty query entries (e.g. `rsid`, `subid`).
 */
export function buildPathfinderUrl(
  base: string,
  path: string,
  query: Record<string, string>,
): string {
  const baseNorm = base.replace(/\/$/, '');
  const pathNorm = path.startsWith('/') ? path : `/${path}`;
  const u = new URL(`${baseNorm}${pathNorm}`);
  for (const [k, v] of Object.entries(query)) {
    if (v) u.searchParams.set(k, v);
  }
  return u.toString();
}

/**
 * Listens on `window` for `{ type: 'PATH_COMPLETE' }` from an iframe. Ignores all messages when
 * `allowedOrigins` is empty or `event.origin` is not in the set.
 *
 * @returns Unsubscribe function (removes the `message` listener).
 */
export function subscribePathfinderComplete(
  allowedOrigins: readonly string[],
  onComplete: () => void,
): () => void {
  const allowed = new Set(allowedOrigins);

  const handler = (ev: MessageEvent) => {
    if (allowed.size === 0) return;
    if (!allowed.has(ev.origin)) return;
    if (!isPathCompleteMessage(ev.data)) return;
    onComplete();
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
